import type { LucideIcon } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

interface NavigationMetricCardAltProps {
  title: string;
  value: string;
  icon: LucideIcon;
  onClick?: () => void;
  variant?: "success" | "warning" | "error" | "info";
  change?: string;
  changeLabel?: string;
  ariaLabel?: string;
}

const variantStyles = {
  success: {
    background: "bg-success-100", // soft green background
    icon: "text-success-600", // success-600 icon color
  },
  warning: {
    background: "bg-warning-100", // soft orange background
    icon: "text-warning-600", // warning-600 icon color
  },
  error: {
    background: "bg-error-100", // soft red background
    icon: "text-error-600", // error-600 icon color
  },
  info: {
    background: "bg-teal-100", // soft teal background
    icon: "text-teal-600", // teal-600 icon color
  },
};

export function NavigationMetricCardAlt({
  title,
  value,
  icon: Icon,
  onClick,
  variant = "success",
  change,
  changeLabel,
  ariaLabel,
}: NavigationMetricCardAltProps) {
  const styles = variantStyles[variant];

  return (
    <Card className="overflow-hidden border-0">
      {/* Colored Top Section - IMPORTANT: Use className with template literal, NOT inline style */}
      <div className={`relative h-24 p-4 ${styles.background}`}>
        <div className="flex items-center justify-between h-full">
          <div className="flex flex-col justify-center">
            <p className="text-neutral-700 text-sm font-medium">{title}</p>
            <p className="text-neutral-900 text-2xl font-bold mt-1">{value}</p>
          </div>
          <div className={`${styles.icon}`}>
            <Icon className="h-10 w-10" strokeWidth={1.5} />
          </div>
        </div>
      </div>

      {/* Bottom Section - With change text and optional "Show me" button */}
      <div className="p-4 bg-card space-y-3">
        {/* Change text - show if either change or changeLabel is provided */}
        {(change || changeLabel) && (
          <div className="text-xs text-muted-foreground">
            {change && changeLabel ? (
              <div className="flex items-center gap-2">
                <span className="font-medium">{change}</span>
                <span>{changeLabel}</span>
              </div>
            ) : (
              <span>{change || changeLabel}</span>
            )}
          </div>
        )}
        
        {/* Show me button - only show if onClick is provided */}
        {onClick && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClick}
            aria-label={ariaLabel}
            className="w-full justify-between text-neutral-700 hover:text-teal-600 hover:bg-teal-50"
          >
            <span className="text-sm font-medium">Show me</span>
            <ArrowRight className="h-4 w-4" />
          </Button>
        )}
      </div>
    </Card>
  );
}
